import {
  Button,
  Container,
  Skeleton,
  VStack,
  Text,
  Textarea,
  Box,
  Badge,
  useToast,
  Input,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { findDispute, sendFileDispute, sendMessageDispute } from 'services/api.service';
import { useState } from 'react';
import { FiUpload } from 'react-icons/fi';

export default function DisputeRequest() {
  const { id } = useParams<{ id: string }>();
  const [disputeMessage, setDisputeMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const toast = useToast();
  const { data: dispute, isLoading } = useQuery({
    queryKey: ['findDispute', id],
    queryFn: () => findDispute(id),
    enabled: !!id,
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileType = file.type;
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];

      if (!validTypes.includes(fileType)) {
        toast({
          title: 'Tipo de arquivo inválido',
          description: 'Por favor, selecione apenas arquivos PDF ou imagens (JPG, PNG)',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        event.target.value = '';
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        toast({
          title: 'Arquivo muito grande',
          description: 'O arquivo deve ter no máximo 5MB',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        event.target.value = '';
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleRequestDispute = () => {
    // TODO: Implement dispute request logic with file upload
    console.log('Requesting dispute for charge:', id, 'with message:', disputeMessage, 'and file:', selectedFile);
    sendMessageDispute(id, { message: disputeMessage })
      .then((res) => {
        if (selectedFile) {
          sendFileDispute(id, { file: selectedFile })
            .then((res) => {
              toast({
                title: 'Arquivo enviado com sucesso',
                description: 'O arquivo foi enviado com sucesso',
                status: 'success',
                duration: 2000,
                isClosable: true,
              });
            })
            .catch((err) => {
              toast({
                title: 'Erro ao enviar arquivo',
                description: err?.response?.data?.message ?? 'Ocorreu um erro ao enviar o arquivo',
                status: 'error',
              });
            });
        } else {
          toast({
            title: 'Mensagem enviada com sucesso',
            description: 'A mensagem foi enviada com sucesso',
            status: 'success',
            duration: 2000,
            isClosable: true,
          });
        }
      })
      .catch((err) => {
        toast({
          title: 'Erro ao enviar mensagem',
          description: err?.response?.data?.message ?? 'Ocorreu um erro ao enviar a mensagem',
          status: 'error',
        });
      });
  };

  if (isLoading) {
    return (
      <Container
        maxW="container.lg"
        py={10}
      >
        <VStack spacing={8}>
          <Skeleton
            height="40px"
            width="60%"
          />
        </VStack>
      </Container>
    );
  }

  return (
    <Container
      maxW="container.md"
      py={10}
    >
      <VStack
        spacing={6}
        align="stretch"
      >
        {dispute && (
          <Box
            p={6}
            borderWidth="1px"
            borderRadius="lg"
            bg="white"
            boxShadow="sm"
          >
            <VStack
              spacing={4}
              align="stretch"
            >
              <Box>
                <Text
                  fontSize="sm"
                  color="gray.500"
                >
                  ID da Disputa
                </Text>
                <Text fontWeight="medium">{dispute.id}</Text>
              </Box>

              <Box>
                <Text
                  fontSize="sm"
                  color="gray.500"
                >
                  Status
                </Text>
                <Badge colorScheme={dispute.status === 'RELEASED' ? 'green' : 'yellow'}>{dispute.status}</Badge>
              </Box>

              <Box>
                <Text
                  fontSize="sm"
                  color="gray.500"
                >
                  Data de Criação
                </Text>
                <Text>{new Date(dispute.createdAt).toLocaleDateString()}</Text>
              </Box>

              {dispute.releaseInDays && (
                <Box>
                  <Text
                    fontSize="sm"
                    color="gray.500"
                  >
                    Liberação em
                  </Text>
                  <Text>{dispute.releaseInDays} dias</Text>
                </Box>
              )}

              {dispute.releaseOn && (
                <Box>
                  <Text
                    fontSize="sm"
                    color="gray.500"
                  >
                    Data de Liberação
                  </Text>
                  <Text>{new Date(dispute.releaseOn).toLocaleDateString()}</Text>
                </Box>
              )}
            </VStack>
          </Box>
        )}

        {dispute?.status === 'RELEASED' && (
          <>
            <Box>
              <Text
                mb={2}
                fontWeight="medium"
              >
                Descreva o motivo da sua disputa
              </Text>
              <Textarea
                value={disputeMessage}
                onChange={(e) => setDisputeMessage(e.target.value)}
                placeholder="Digite aqui os detalhes da sua disputa..."
                size="lg"
                minH="150px"
              />
            </Box>

            <Box>
              <Text
                mb={2}
                fontWeight="medium"
              >
                Anexar documento (PDF ou imagem)
              </Text>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                display="none"
                id="file-upload"
              />
              <HStack spacing={4}>
                <Button
                  as="label"
                  htmlFor="file-upload"
                  leftIcon={<Icon as={FiUpload} />}
                  variant="outline"
                  cursor="pointer"
                >
                  Selecionar arquivo
                </Button>
                {selectedFile && (
                  <Text
                    fontSize="sm"
                    color="gray.600"
                  >
                    {selectedFile.name}
                  </Text>
                )}
              </HStack>
              <Text
                fontSize="xs"
                color="gray.500"
                mt={2}
              >
                Formatos aceitos: PDF, JPG, PNG. Tamanho máximo: 5MB
              </Text>
            </Box>

            <Button
              colorScheme="brand"
              size="lg"
              onClick={handleRequestDispute}
              isDisabled={!dispute || !disputeMessage.trim()}
            >
              Solicitar Disputa
            </Button>
          </>
        )}
      </VStack>
    </Container>
  );
}
